// Walker saga — drives the simulation playback engine.
//
// The walker consumes events from the seed store (or a subscription channel),
// resolves each event to a topology node EVENT-FIRST (payload.nodeId →
// eventRouting → expectedPath only as a visible last resort), dispatches
// simulationStepCompleted, evaluates branch predicates, and runs assertion
// checks. Two index spaces are kept strictly separate:
//   - EVENT index (walkerPosition): counts every event processed;
//   - PATH cursor (actualPath.length): counts only path events, and is the
//     index used against expectedPath and in assertion ids.

import {
  take,
  put,
  select,
  call,
  delay,
  race,
  cancelled,
  fork,
  takeLatest,
} from 'redux-saga/effects';
import type { PlatformEvent, ConnectionDef, BranchPredicate, NodeDef } from '../../types/index.ts';
import { evaluateSagaTransitions, evaluateSagaCompensation } from './sagaBehavioralEvaluator.ts';
import { evaluatePolicyChains } from './policyChainEvaluator.ts';
import {
  getCachedSagaDefinitions,
  getCachedPolicyDefinitions,
} from '../../services/ArtifactLoaderService.ts';
import type { RootState } from '../index.ts';
import { loadScenario } from '../actions/loadScenario.ts';
import {
  resolveNodeId,
  selectEventRouting,
  selectBranchPredicates,
  selectNodeById,
  selectFrames,
} from '../slices/topologySlice.ts';
import {
  runAllAssertionsForStep,
  assertPathSequence,
  skipAssertion,
  type StepAssertionContext,
  type AssertionOutput,
} from './assertionEngine.ts';
import {
  buildTopologyContext,
  diagnosePathStep,
  diagnosePathSequence,
  type TopologyContext,
} from './diagnosticEngine.ts';
import type { AssertionDiagnosis } from '../../types/assertion-diagnostics.ts';
import type { SimulationTopology, FrameDef } from '../../types/index.ts';

// ---------------------------------------------------------------------------
// Shared selector — builds a SimulationTopology-shaped object from Redux
// for diagnostic context. Extracted to avoid duplication.
// ---------------------------------------------------------------------------

function selectTopologyForDiagnosis(state: RootState): SimulationTopology {
  return {
    lanes: state.topology.lanes.concat(state.topology.branchLanes),
    frames: state.topology.frames,
    connections: state.topology.connections,
    eventRouting: state.topology.eventRouting,
    branchPredicates: state.topology.branchPredicates,
  };
}

// Module-level cache for the diagnostic TopologyContext. Built once per run
// and reused across all assertion dispatches within the run. Invalidated on
// loadScenario (handleScenarioLoaded) so each scenario starts fresh. This
// avoids rebuilding maps for every step of a scenario with 100+ events.
//
// Cache key tracks ALL topology references consumed by buildTopologyContext:
// frames, lanes, branchLanes, eventRouting, and branchPredicates. Any
// reference change invalidates the cache. Live topology edits that replace
// these references (via slice reducers) will be picked up on the next step.
interface TopoCacheKey {
  frames: unknown;
  lanes: unknown;
  branchLanes: unknown;
  eventRouting: unknown;
  branchPredicates: unknown;
}
let cachedTopoCtx: TopologyContext | null = null;
let cachedTopoKey: TopoCacheKey | null = null;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function* getTopologyContext(): Generator<any, TopologyContext, any> {
  const key: TopoCacheKey = yield select((state: RootState) => ({
    frames: state.topology.frames,
    lanes: state.topology.lanes,
    branchLanes: state.topology.branchLanes,
    eventRouting: state.topology.eventRouting,
    branchPredicates: state.topology.branchPredicates,
  }));

  if (
    cachedTopoCtx &&
    cachedTopoKey &&
    cachedTopoKey.frames === key.frames &&
    cachedTopoKey.lanes === key.lanes &&
    cachedTopoKey.branchLanes === key.branchLanes &&
    cachedTopoKey.eventRouting === key.eventRouting &&
    cachedTopoKey.branchPredicates === key.branchPredicates
  ) {
    return cachedTopoCtx;
  }

  const topoState: SimulationTopology = yield select(selectTopologyForDiagnosis);
  cachedTopoCtx = buildTopologyContext(topoState);
  cachedTopoKey = key;
  return cachedTopoCtx;
}

import {
  selectEvents,
  selectExpectedPath,
  selectTotalEvents,
  selectActiveBranches,
} from '../slices/eventsSlice.ts';
import {
  simulationStarted,
  simulationStepCompleted,
  advanceWalkerPosition,
  simulationCompleted,
  simulationFailed,
  simulationPaused,
  simulationResumed,
  assertionPassed,
  assertionFailed,
  assertionSkipped,
  scrubToPosition,
  selectSimulationStatus,
  selectWalkerPosition,
  selectActualPath,
  selectAssertionResults,
  selectActivatedBranchRecords,
  accumulateCoverage,
  selectAssertionSummary,
  type BranchActivationRecord,
} from '../slices/simulationSlice.ts';
import {
  selectPlaybackMode,
  selectPlaybackSpeed,
  selectSidebarOpen,
  toggleSidebar,
} from '../slices/uiSlice.ts';
import { selectScenarioId } from '../slices/eventsSlice.ts';

// ---------------------------------------------------------------------------
// Action types used as saga signals
// ---------------------------------------------------------------------------

export const WALKER_START = 'walker/start' as const;
export const WALKER_STEP_FORWARD = 'walker/stepForward' as const;
export const WALKER_SCRUB_TO = 'walker/scrubTo' as const;
export const WALKER_STOP = 'walker/stop' as const;

export interface WalkerStartAction {
  type: typeof WALKER_START;
  payload?: { fromPosition: number };
  [key: string]: unknown;
}

export interface WalkerStepForwardAction {
  type: typeof WALKER_STEP_FORWARD;
  [key: string]: unknown;
}

export interface WalkerScrubToAction {
  type: typeof WALKER_SCRUB_TO;
  payload: { position: number };
  [key: string]: unknown;
}

export interface WalkerStopAction {
  type: typeof WALKER_STOP;
  [key: string]: unknown;
}

export function walkerStart(fromPosition?: number): WalkerStartAction {
  return {
    type: WALKER_START,
    payload: fromPosition != null ? { fromPosition } : undefined,
  };
}

export function walkerStepForward(): WalkerStepForwardAction {
  return { type: WALKER_STEP_FORWARD };
}

export function walkerScrubTo(position: number): WalkerScrubToAction {
  return { type: WALKER_SCRUB_TO, payload: { position } };
}

export function walkerStop(): WalkerStopAction {
  return { type: WALKER_STOP };
}

// ---------------------------------------------------------------------------
// Frame helpers — Moment node IDs are `frame::lane::Node::branch`
// ---------------------------------------------------------------------------

/** The frame id of a full Moment node ID (substring before the first `::`). */
export function frameIdOf(nodeId: string): string {
  const idx = nodeId.indexOf('::');
  return idx === -1 ? nodeId : nodeId.slice(0, idx);
}

/** Parse the moment index out of a `moment-N-...` frame id, or null. */
export function momentIndexOf(frameId: string): number | null {
  const m = frameId.match(/^moment-(\d+)/);
  return m ? parseInt(m[1], 10) : null;
}

// ---------------------------------------------------------------------------
// Node resolution — maps an event to a topology node, EVENT-FIRST
// ---------------------------------------------------------------------------

/**
 * Determines whether an event type is a non-path synthetic event.
 * SimSaga.* events are injected by Moment (appended after path events, but
 * tolerated mid-stream) and do not occupy positions in expectedPath. They
 * must be processed but never advance the path cursor.
 */
export function isNonPathEvent(eventType: string): boolean {
  return eventType.startsWith('SimSaga.');
}

/**
 * Determines whether an event type represents a terminal failure.
 * SimFailure.* events are generated by Moment for negative scenarios
 * (precondition violations) and should terminate the simulation.
 */
export function isFailureEvent(eventType: string): boolean {
  return eventType.startsWith('SimFailure.');
}

export type NodeResolutionMethod = 'payload' | 'routing' | 'expected-path' | 'unresolved';

export interface NodeResolution {
  nodeId: string | null;
  /**
   * How the node was determined. 'expected-path' means the comparison against
   * expectedPath is vacuous — callers must surface a resolution-fallback skip.
   */
  method: NodeResolutionMethod;
}

/**
 * Resolve the OBSERVED node for an event. Order matters for falsifiability:
 * 1. payload.nodeId — the event's own claim (artifact contract);
 * 2. eventRouting[eventType] — topology ground truth; with multiple targets,
 *    prefer one whose frame matches the expected frame at the path cursor;
 * 3. expectedPath[pathCursor] — LAST resort only, flagged so the resulting
 *    path assertion is emitted as an explicit skip, never a vacuous pass.
 *
 * `pathCursor` is the PATH-CURSOR index (actualPath.length), not the raw
 * event index.
 */
export function resolveEventToNode(
  event: PlatformEvent,
  expectedPath: string[],
  pathCursor: number,
  eventRouting: Record<string, string[]>,
): NodeResolution {
  const payload = event.payload as Record<string, unknown>;
  if (typeof payload.nodeId === 'string' && payload.nodeId) {
    return { nodeId: payload.nodeId, method: 'payload' };
  }

  const targets = eventRouting[event.eventType];
  if (targets && targets.length > 0) {
    if (targets.length > 1 && pathCursor < expectedPath.length) {
      const expectedFrame = frameIdOf(expectedPath[pathCursor]);
      const frameMatch = targets.find((t) => frameIdOf(t) === expectedFrame);
      if (frameMatch) return { nodeId: frameMatch, method: 'routing' };
    }
    return { nodeId: targets[0], method: 'routing' };
  }

  if (pathCursor < expectedPath.length) {
    return { nodeId: expectedPath[pathCursor], method: 'expected-path' };
  }

  return { nodeId: null, method: 'unresolved' };
}

// ---------------------------------------------------------------------------
// Branch evaluation — determines which route a branch node takes
// ---------------------------------------------------------------------------

export function evaluateBranchPredicate(event: PlatformEvent, predicate: BranchPredicate): string {
  const payload = event.payload as Record<string, unknown>;
  const fieldValue = String(payload[predicate.field] ?? '');
  return predicate.routes[fieldValue] ?? predicate.defaultRoute;
}

/**
 * Evaluate a branch predicate against the raw event payload, producing a
 * structured record. Returns null when the payload carries no predicate
 * field (old-format artifacts) — callers must NOT fabricate a default-route
 * activation in that case.
 */
export function evaluateBranchRecord(
  event: PlatformEvent,
  frameId: string,
  predicate: BranchPredicate,
): BranchActivationRecord | null {
  const payload = event.payload as Record<string, unknown>;
  const raw = payload[predicate.field];
  if (raw === undefined || raw === null) return null;
  const condition = String(raw);
  return {
    frameId,
    condition,
    route: predicate.routes[condition] ?? predicate.defaultRoute,
  };
}

// ---------------------------------------------------------------------------
// Connection resolution — frame-level (Moment artifact contract)
// ---------------------------------------------------------------------------

export interface ConnectionResolution {
  connection: ConnectionDef | null;
  /** Set when a connection may exist but is not verifiable (old format). */
  skipReason?: string;
}

/**
 * Resolve the active connection for a step by (frame of current node,
 * event.eventType). Moment connections are frame-level: `from` is a frame id
 * and each connection carries the eventType that traverses it. When
 * candidate connections omit eventType (old-format topology), the boundary/
 * schema tiers degrade to explicit skips rather than silently matching.
 */
export function resolveFrameConnection(
  frameId: string,
  eventType: string,
  connections: ConnectionDef[],
): ConnectionResolution {
  const candidates = connections.filter((c) => c.from === frameId);
  if (candidates.length === 0) return { connection: null };

  const typed = candidates.filter((c) => typeof c.eventType === 'string');
  const match = typed.find((c) => c.eventType === eventType);
  if (match) return { connection: match };

  if (typed.length < candidates.length) {
    return {
      connection: null,
      skipReason: `connection(s) from frame "${frameId}" carry no eventType (old-format topology) — boundary/schema tiers skipped`,
    };
  }
  // All candidates are typed and none matches: this event does not traverse
  // a declared crossing — nothing to assert.
  return { connection: null };
}

/**
 * Cross-frame transition conformance check. Consecutive path nodes must be
 * in the same frame, the next-sequential frame, or linked by a 'return'
 * style edge. This is the check that catches Moment's returns-to class of
 * bug. Returns null for same-frame steps (nothing to assert).
 */
export function assertFrameTransition(
  pathCursor: number,
  previousNodeId: string | null,
  nodeId: string,
  connections: ConnectionDef[],
  knownFrameIds: ReadonlySet<string>,
): AssertionOutput | null {
  if (!previousNodeId) return null;
  const prevFrame = frameIdOf(previousNodeId);
  const curFrame = frameIdOf(nodeId);
  if (prevFrame === curFrame) return null;

  const assertionId = `topology-conformance-${pathCursor}`;
  const prevIdx = momentIndexOf(prevFrame);
  const curIdx = momentIndexOf(curFrame);

  if (prevIdx === null || curIdx === null) {
    return skipAssertion(
      assertionId,
      'topology_conformance',
      `Step ${pathCursor}: frames "${prevFrame}" → "${curFrame}" carry no moment index — transition not verifiable`,
    );
  }

  if (curIdx === prevIdx || curIdx === prevIdx + 1) {
    return {
      assertionId,
      assertionType: 'topology_conformance',
      passed: true,
      detail: `Step ${pathCursor}: frame transition "${prevFrame}" → "${curFrame}" is sequential`,
    };
  }

  const returnEdges = connections.filter((c) => c.style === 'return' && c.from === prevFrame);
  if (returnEdges.some((c) => c.to === curFrame)) {
    return {
      assertionId,
      assertionType: 'topology_conformance',
      passed: true,
      detail: `Step ${pathCursor}: non-sequential transition "${prevFrame}" → "${curFrame}" is declared by a return edge`,
    };
  }
  if (returnEdges.some((c) => !knownFrameIds.has(c.to))) {
    return skipAssertion(
      assertionId,
      'topology_conformance',
      `Step ${pathCursor}: return edge from "${prevFrame}" targets a non-frame id (old-format topology) — transition not verifiable`,
    );
  }

  return {
    assertionId,
    assertionType: 'topology_conformance',
    passed: false,
    detail: `Step ${pathCursor}: non-sequential frame transition "${prevFrame}" (moment ${prevIdx}) → "${curFrame}" (moment ${curIdx}) with no declared return edge — returns-to violation`,
  };
}

// ---------------------------------------------------------------------------
// Replay-state computation — used for backward scrub
// ---------------------------------------------------------------------------

export interface ReplayedState {
  actualPath: string[];
  activatedBranches: string[];
  activatedBranchRecords: BranchActivationRecord[];
  activeConnections: string[];
}

/**
 * Compute path/branch/connection state for a given EVENT position by
 * replaying events through the same event-first resolution the walker uses.
 * Non-path events advance the event position but not the path cursor.
 */
export function computeStateAtPosition(
  events: PlatformEvent[],
  expectedPath: string[],
  position: number,
  eventRouting: Record<string, string[]>,
  branchPredicates: Record<string, BranchPredicate>,
  connections: ConnectionDef[],
): ReplayedState {
  const actualPath: string[] = [];
  const activatedBranches: string[] = [];
  const activatedBranchRecords: BranchActivationRecord[] = [];
  let activeConnections: string[] = [];

  const end = Math.min(Math.max(0, position), events.length);
  for (let i = 0; i < end; i++) {
    const event = events[i];
    if (isNonPathEvent(event.eventType)) continue;
    if (isFailureEvent(event.eventType)) break;

    const resolution = resolveEventToNode(event, expectedPath, actualPath.length, eventRouting);
    if (!resolution.nodeId) continue;
    const nodeId = resolution.nodeId;
    const prevNode = actualPath.length > 0 ? actualPath[actualPath.length - 1] : null;
    actualPath.push(nodeId);

    const frameId = frameIdOf(nodeId);
    const predicate = branchPredicates[frameId] ?? branchPredicates[nodeId];
    if (predicate) {
      const record = evaluateBranchRecord(event, frameId, predicate);
      if (record) {
        if (!activatedBranches.includes(record.route)) {
          activatedBranches.push(record.route);
        }
        if (
          !activatedBranchRecords.some(
            (r) => r.frameId === record.frameId && r.condition === record.condition,
          )
        ) {
          activatedBranchRecords.push(record);
        }
      }
    }

    activeConnections = resolveActiveConnections(prevNode, nodeId, connections);
  }

  return { actualPath, activatedBranches, activatedBranchRecords, activeConnections };
}

export function resolveActiveConnections(
  fromNodeId: string | null,
  toNodeId: string,
  connections: ConnectionDef[],
): string[] {
  if (!fromNodeId) return [];
  const nodeLevel = connections
    .filter((c) => c.from === fromNodeId && c.to === toNodeId)
    .map((c) => `${c.from}->${c.to}`);
  if (nodeLevel.length > 0) return nodeLevel;
  // Frame-level topologies (full `frame::lane::Node::branch` ids): highlight
  // the crossing edge(s) of the frame being left.
  if (fromNodeId.includes('::') && toNodeId.includes('::')) {
    const fromFrame = frameIdOf(fromNodeId);
    const toFrame = frameIdOf(toNodeId);
    if (fromFrame !== toFrame) {
      return connections.filter((c) => c.from === fromFrame).map((c) => `${c.from}->${c.to}`);
    }
  }
  return [];
}

// ---------------------------------------------------------------------------
// Assertion dispatch — emits results from the assertion engine
// ---------------------------------------------------------------------------

function* dispatchAssertionResults(
  results: AssertionOutput[],
  topoCtx?: TopologyContext,
  enrichmentCtx?: {
    expectedPath: string[];
    actualPath?: string[];
    actualNodeId?: string;
    stepIndex?: number;
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- redux-saga Generator types require `any` for mixed yield effects
): Generator<any, void, any> {
  for (const result of results) {
    if (result.skipped) {
      yield put(
        assertionSkipped({
          assertionId: result.assertionId,
          assertionType: result.assertionType,
          detail: result.detail,
        }),
      );
      continue;
    }

    // Enrich failed path assertions with diagnostics
    let diagnosis: AssertionDiagnosis | undefined = undefined;
    if (!result.passed && topoCtx && enrichmentCtx) {
      if (
        result.assertionType === 'path' &&
        result.assertionId.startsWith('path-step-') &&
        enrichmentCtx.actualNodeId != null &&
        enrichmentCtx.stepIndex != null
      ) {
        diagnosis = diagnosePathStep(
          result,
          enrichmentCtx.expectedPath,
          enrichmentCtx.actualNodeId,
          enrichmentCtx.stepIndex,
          topoCtx,
        );
      } else if (
        result.assertionType === 'path' &&
        result.assertionId === 'path-sequence' &&
        enrichmentCtx.actualPath
      ) {
        diagnosis = diagnosePathSequence(
          result,
          enrichmentCtx.actualPath,
          enrichmentCtx.expectedPath,
          topoCtx,
        );
      }
    }

    if (result.passed) {
      yield put(
        assertionPassed({
          assertionId: result.assertionId,
          assertionType: result.assertionType,
          detail: result.detail,
        }),
      );
    } else {
      yield put(
        assertionFailed({
          assertionId: result.assertionId,
          assertionType: result.assertionType,
          detail: result.detail,
          diagnosis,
        }),
      );
    }
  }
}

// ---------------------------------------------------------------------------
// Completion helper — runs terminal assertions then completes
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function* runCompletionAssertions(): Generator<any, void, any> {
  const expectedPath: string[] = yield select(selectExpectedPath);
  if (expectedPath.length > 0) {
    const actualPath: string[] = yield select(selectActualPath);
    const result = assertPathSequence(actualPath, expectedPath);

    // Build topology context (cached across steps of the same run)
    const topoCtx: TopologyContext = yield* getTopologyContext();
    yield* dispatchAssertionResults([result], topoCtx, { expectedPath, actualPath });
  }

  // Branch coverage — compare accumulated activations against the scenario's
  // DECLARED activeBranches (ground truth). If branch evaluations were
  // skipped (old-format artifacts), the comparison is explicitly skipped too.
  const declaredBranches: { condition: string }[] = yield select(selectActiveBranches);
  const branchRecords: BranchActivationRecord[] = yield select(selectActivatedBranchRecords);
  const allResults: { skipped?: boolean; assertionType: string }[] =
    yield select(selectAssertionResults);
  const declaredConditions = declaredBranches.map((b) => b.condition);
  if (declaredConditions.length > 0 || branchRecords.length > 0) {
    const branchEvalSkipped = allResults.some(
      (r) => r.skipped && r.assertionType === 'branch_evaluation',
    );
    if (branchEvalSkipped) {
      yield put(
        assertionSkipped({
          assertionId: 'branch-coverage',
          assertionType: 'branch_evaluation',
          detail:
            'Branch evaluations were skipped during the run (old-format artifact) — declared activeBranches cannot be verified',
        }),
      );
    } else {
      const activatedConditions = branchRecords.map((r) => r.condition);
      const missing = declaredConditions.filter((c) => !activatedConditions.includes(c));
      const unexpected = activatedConditions.filter((c) => !declaredConditions.includes(c));
      for (const c of missing) {
        yield put(
          assertionFailed({
            assertionId: `branch-coverage-missing-${c}`,
            assertionType: 'branch_evaluation',
            detail: `Declared active branch "${c}" was never activated during the run`,
          }),
        );
      }
      for (const c of unexpected) {
        yield put(
          assertionFailed({
            assertionId: `branch-coverage-unexpected-${c}`,
            assertionType: 'branch_evaluation',
            detail: `Branch "${c}" was activated but is not declared in the scenario's activeBranches`,
          }),
        );
      }
      if (missing.length === 0 && unexpected.length === 0) {
        yield put(
          assertionPassed({
            assertionId: 'branch-coverage',
            assertionType: 'branch_evaluation',
            detail: `Activated branches match declared activeBranches (${declaredConditions.length} branch decision(s))`,
          }),
        );
      }
    }
  }

  // Run saga behavioral assertions (saga_transition + saga_compensation).
  // Definitions are read source-aware: a websocket run must never evaluate
  // another source's cached defs (they describe a different project).
  const allEvents: PlatformEvent[] = yield select(selectEvents);
  const activeSource: 'example' | 'user' | 'websocket' | undefined = yield select(
    (state: RootState) => (state as Partial<RootState>).scenarioSource?.activeSource,
  );
  const sagaDefs = getCachedSagaDefinitions(activeSource);
  const sagaTransitionResults = evaluateSagaTransitions(allEvents, sagaDefs ?? undefined);
  if (sagaTransitionResults.length > 0) {
    yield* dispatchAssertionResults(sagaTransitionResults);
  }
  if (sagaDefs && sagaDefs.length > 0) {
    const sagaCompResults = evaluateSagaCompensation(allEvents, sagaDefs);
    if (sagaCompResults.length > 0) {
      yield* dispatchAssertionResults(sagaCompResults);
    }
  }

  // Run policy chain assertions (policy_chain tier). Absent definitions
  // artifact (null) → one explicit tier SKIP record from the evaluator;
  // present definitions → real trigger→command causal-link assertions.
  const policyDefs = getCachedPolicyDefinitions(activeSource);
  const policyResults = evaluatePolicyChains(allEvents, policyDefs);
  if (policyResults.length > 0) {
    yield* dispatchAssertionResults(policyResults);
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function* completeSimulation(): Generator<any, void, any> {
  // Error containment: a throwing assertion phase must surface as a FAILED
  // run (verdict 'error'), never brick the walker or read as a pass.
  try {
    yield* runCompletionAssertions();
  } catch (err) {
    console.error('[Walker] completion assertions threw:', err);
    yield put(simulationFailed());
    return;
  }

  yield put(simulationCompleted());

  // Accumulate coverage from this scenario run — OBSERVED path and
  // frame-scoped branch route keys (`frameId:condition`).
  const scenarioId: string | null = yield select(selectScenarioId);
  const actualPathFinal: string[] = yield select(selectActualPath);
  const branchRecords: BranchActivationRecord[] = yield select(selectActivatedBranchRecords);
  if (scenarioId) {
    yield put(
      accumulateCoverage({
        scenarioId,
        nodes: actualPathFinal,
        branches: branchRecords.map((r) => `${r.frameId}:${r.condition}`),
      }),
    );
  }

  // Auto-open sidebar when there are failures
  const summary: { failed: number } = yield select(selectAssertionSummary);
  const sidebarOpen: boolean = yield select(selectSidebarOpen);
  if (summary.failed > 0 && !sidebarOpen) {
    yield put(toggleSidebar());
  }
}

// ---------------------------------------------------------------------------
// Single step execution
// ---------------------------------------------------------------------------

function* executeStep(stepIndex: number) {
  const events: PlatformEvent[] = yield select(selectEvents);
  const expectedPath: string[] = yield select(selectExpectedPath);
  const eventRouting: Record<string, string[]> = yield select(selectEventRouting);
  const branchPredicates: Record<string, BranchPredicate> = yield select(selectBranchPredicates);
  const actualPath: string[] = yield select(selectActualPath);

  // The PATH cursor: index into expectedPath. Non-path events advance the
  // event position (stepIndex/walkerPosition) but never this cursor.
  const pathCursor = actualPath.length;

  if (stepIndex >= events.length) {
    yield* completeSimulation();
    return false;
  }

  const event = events[stepIndex];

  // Non-path events (SimSaga.*) advance walkerPosition but don't accumulate
  // in actualPath or trigger per-step assertions — whether trailing or
  // interleaved mid-stream.
  if (isNonPathEvent(event.eventType)) {
    yield put(advanceWalkerPosition());
    return true;
  }

  // SimFailure events terminate the simulation immediately.
  // Don't dispatch simulationStepCompleted — the failure event occupies no
  // path position and would corrupt path assertions.
  if (isFailureEvent(event.eventType)) {
    yield* completeSimulation();
    return false;
  }

  const previousNode = pathCursor > 0 ? actualPath[pathCursor - 1] : null;

  // Resolve the OBSERVED node event-first (payload.nodeId → eventRouting →
  // expectedPath fallback, which is surfaced as a skip).
  const resolution = resolveEventToNode(event, expectedPath, pathCursor, eventRouting);
  if (!resolution.nodeId) {
    console.warn('[Walker] Cannot resolve event to node:', {
      eventType: event.eventType,
      stepIndex,
      pathCursor,
      expectedPathLength: expectedPath.length,
      eventRoutingKeys: Object.keys(eventRouting),
    });
    yield put(simulationFailed());
    return false;
  }
  const nodeId = resolution.nodeId;

  // Branch predicate lookup — keyed by FRAME id (prefix before the first
  // '::'). Legacy fallbacks: raw node id and the resolved topology node's
  // frame (for scenario-namespace ids).
  const topoNodeId: string = yield select((state: RootState) => resolveNodeId(state, nodeId));
  const frameId = frameIdOf(nodeId);
  const predicate =
    branchPredicates[frameId] ??
    branchPredicates[nodeId] ??
    branchPredicates[frameIdOf(topoNodeId)] ??
    branchPredicates[topoNodeId];

  let branchActivated: string | undefined;
  let branchRecord: BranchActivationRecord | undefined;
  if (predicate) {
    // Never fabricate a default-route activation when the event carries no
    // predicate field (old-format artifacts) — that would be unobserved data.
    const record = evaluateBranchRecord(event, frameId, predicate);
    if (record) {
      branchActivated = record.route;
      branchRecord = record;
    }
  }

  // Resolve connections (visual state)
  const allConnections: ConnectionDef[] = yield select(
    (state: { topology: { connections: ConnectionDef[] } }) => state.topology.connections,
  );
  const connectionIds = resolveActiveConnections(previousNode, nodeId, allConnections);

  // Dispatch step
  yield put(
    simulationStepCompleted({
      nodeId,
      connectionIds: connectionIds.length > 0 ? connectionIds : undefined,
      branchActivated,
      branchRecord,
    }),
  );

  // Run all assertion tiers, indexed by the PATH cursor. Steps beyond
  // expectedPath.length produce real path FAILURES (extra path events are a
  // divergence, not silence). Discovery mode (no expectedPath) runs none.
  if (expectedPath.length > 0) {
    const previousEvent = stepIndex > 0 ? events[stepIndex - 1] : null;

    // Boundary/schema connection — frame-level resolution by
    // (frame of current node, event.eventType).
    const connectionRes = resolveFrameConnection(frameId, event.eventType, allConnections);

    const node: NodeDef | undefined = yield select((state: RootState) =>
      selectNodeById(state, nodeId),
    );
    const frames: FrameDef[] = yield select(selectFrames);
    const frameDef = frames.find((f) => f.id === frameId);
    const predicateSkipReason =
      !predicate && frameDef?.isBranch
        ? `frame "${frameId}" is a branch frame but declares no branch predicate — branch evaluation skipped`
        : undefined;

    const totalSteps: number = yield select(selectTotalEvents);

    const ctx: StepAssertionContext = {
      stepIndex: pathCursor,
      nodeId,
      node,
      event,
      previousEvent,
      connection: connectionRes.connection,
      connectionSkipReason: connectionRes.skipReason,
      expectedPath,
      predicate,
      predicateSkipReason,
      resolutionFallbackReason:
        resolution.method === 'expected-path'
          ? `event "${event.eventType}" carries no payload.nodeId and no eventRouting entry — observed node taken from expectedPath itself`
          : undefined,
      isLastStep: pathCursor === expectedPath.length - 1,
      totalSteps: expectedPath.length > 0 ? expectedPath.length : totalSteps,
      terminatedByDeclaredFailure: isFailureEvent(events[stepIndex + 1]?.eventType ?? ''),
    };

    const results = runAllAssertionsForStep(ctx);

    // Topology conformance — cross-frame transition legality
    const knownFrameIds = new Set(frames.map((f) => f.id));
    const conformance = assertFrameTransition(
      pathCursor,
      previousNode,
      nodeId,
      allConnections,
      knownFrameIds,
    );
    if (conformance) results.push(conformance);

    // Build topology context (cached across steps of the same run)
    const topoCtx: TopologyContext = yield* getTopologyContext();
    yield* dispatchAssertionResults(results, topoCtx, {
      expectedPath,
      actualNodeId: nodeId,
      stepIndex: pathCursor,
    });
  }

  return true;
}

/**
 * Error-contained step execution: a malformed scenario/topology must fail
 * the RUN (verdict 'error'), never throw out of the playback loops and brick
 * the walker.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function* safeExecuteStep(stepIndex: number): Generator<any, boolean, any> {
  try {
    const ok: boolean = yield call(executeStep, stepIndex);
    return ok;
  } catch (err) {
    console.error('[Walker] executeStep threw at event index', stepIndex, err);
    yield put(simulationFailed());
    return false;
  }
}

// ---------------------------------------------------------------------------
// Scrub handler — forward scrubs REPLAY real steps; backward scrubs truncate
// the RECORDED actualPath. Nothing is fabricated from expectedPath.
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function* handleScrub(scrubAction: WalkerScrubToAction): Generator<any, void, any> {
  const events: PlatformEvent[] = yield select(selectEvents);
  const target = Math.max(0, Math.min(scrubAction.payload.position, events.length));
  const current: number = yield select(selectWalkerPosition);

  if (target > current) {
    // Forward: replay real steps through executeStep (same machinery as
    // WALKER_START fromPosition) so actualPath stays observed, not assumed.
    for (let i = current; i < target; i++) {
      const ok: boolean = yield call(safeExecuteStep, i);
      if (!ok) return;
    }
    return;
  }

  if (target === current) return;

  // Backward: truncate the RECORDED actualPath to the number of path events
  // within events[0..target), and recompute branch/connection state.
  const expectedPath: string[] = yield select(selectExpectedPath);
  const eventRouting: Record<string, string[]> = yield select(selectEventRouting);
  const branchPredicates: Record<string, BranchPredicate> = yield select(selectBranchPredicates);
  const connections: ConnectionDef[] = yield select(
    (state: { topology: { connections: ConnectionDef[] } }) => state.topology.connections,
  );

  const replay = computeStateAtPosition(
    events,
    expectedPath,
    target,
    eventRouting,
    branchPredicates,
    connections,
  );
  const recorded: string[] = yield select(selectActualPath);
  const truncated = recorded.slice(0, replay.actualPath.length);

  yield put(
    scrubToPosition({
      actualPath: truncated,
      position: target,
      activatedBranches: replay.activatedBranches,
      activatedBranchRecords: replay.activatedBranchRecords,
      activeConnections: replay.activeConnections,
    }),
  );
}

// ---------------------------------------------------------------------------
// Playback loop — unified loop handling both auto and manual modes,
// as well as pause/resume transitions.
// ---------------------------------------------------------------------------

function* playbackLoop() {
  while (true) {
    const status: string = yield select(selectSimulationStatus);

    if (status === 'completed' || status === 'failed') break;

    // ── Paused: wait for resume, stop, step, scrub, or restart ──
    if (status === 'paused') {
      const action: { type: string } = yield take([
        'simulation/simulationResumed',
        WALKER_STOP,
        WALKER_STEP_FORWARD,
        WALKER_SCRUB_TO,
        'ui/setPlaybackMode',
        WALKER_START,
        loadScenario.type,
      ]);
      if (action.type === loadScenario.type) {
        // New scenario loaded — exit playback; autoRunWatcher handles instant run
        break;
      }
      if (action.type === WALKER_START) {
        // Replay requested — restart, fast-forward if fromPosition given
        const sa = action as unknown as WalkerStartAction;
        yield put(simulationStarted());
        const from = sa.payload?.fromPosition ?? 0;
        for (let i = 0; i < from; i++) {
          const ok: boolean = yield call(safeExecuteStep, i);
          if (!ok) break;
        }
        continue;
      }
      if (action.type === WALKER_STOP) {
        yield* completeSimulation();
        break;
      }
      if (action.type === WALKER_STEP_FORWARD) {
        // Resume briefly to execute the step, then the loop will
        // re-check mode/status at the top.
        yield put(simulationResumed());
        const pos: number = yield select(selectWalkerPosition);
        const total: number = yield select(selectTotalEvents);
        if (pos >= total) {
          yield* completeSimulation();
          break;
        }
        const ok: boolean = yield call(safeExecuteStep, pos);
        if (!ok) break;
        // After stepping, pause again so user stays in control
        yield put(simulationPaused());
        continue;
      }
      if (action.type === WALKER_SCRUB_TO) {
        yield* handleScrub(action as unknown as WalkerScrubToAction);
      }
      continue;
    }

    const mode: string = yield select(selectPlaybackMode);

    // ── Manual mode: wait for explicit signals ──
    if (mode !== 'auto') {
      const action: { type: string } = yield take([
        WALKER_STEP_FORWARD,
        WALKER_STOP,
        WALKER_SCRUB_TO,
        'ui/setPlaybackMode',
        'simulation/simulationPaused',
        WALKER_START,
        loadScenario.type,
      ]);

      if (action.type === loadScenario.type) {
        // New scenario loaded — exit playback; autoRunWatcher handles instant run
        break;
      }
      if (action.type === WALKER_START) {
        // Replay requested — restart, fast-forward if fromPosition given
        const sa = action as unknown as WalkerStartAction;
        yield put(simulationStarted());
        const from = sa.payload?.fromPosition ?? 0;
        for (let i = 0; i < from; i++) {
          const ok: boolean = yield call(safeExecuteStep, i);
          if (!ok) break;
        }
        continue;
      }
      if (action.type === WALKER_STOP) {
        yield* completeSimulation();
        break;
      } else if (action.type === WALKER_STEP_FORWARD) {
        const position: number = yield select(selectWalkerPosition);
        const totalEvents: number = yield select(selectTotalEvents);
        if (position >= totalEvents) {
          yield* completeSimulation();
          break;
        }
        const continued: boolean = yield call(safeExecuteStep, position);
        if (!continued) break;
      } else if (action.type === WALKER_SCRUB_TO) {
        yield* handleScrub(action as unknown as WalkerScrubToAction);
      }
      // mode change or pause: loop back to top to re-check
      continue;
    }

    // ── Auto mode: advance one step then delay ──
    const position: number = yield select(selectWalkerPosition);
    const totalEvents: number = yield select(selectTotalEvents);

    if (position >= totalEvents) {
      yield* completeSimulation();
      break;
    }

    const continued: boolean = yield call(safeExecuteStep, position);
    if (!continued) break;

    const speed: number = yield select(selectPlaybackSpeed);
    const delayMs = Math.max(100, 1000 / speed);

    // Race the delay against control signals so pause/stop/mode-change/scrub
    // are handled immediately instead of being lost during the wait.
    const {
      stopped,
      stepped,
      restarted,
      scrubbed,
      scenarioLoaded,
    }: {
      stopped?: WalkerStopAction;
      stepped?: WalkerStepForwardAction;
      restarted?: WalkerStartAction;
      scrubbed?: WalkerScrubToAction;
      scenarioLoaded?: unknown;
    } = yield race({
      timer: delay(delayMs),
      stopped: take(WALKER_STOP),
      paused: take('simulation/simulationPaused'),
      modeChanged: take('ui/setPlaybackMode'),
      stepped: take(WALKER_STEP_FORWARD),
      restarted: take(WALKER_START),
      scrubbed: take(WALKER_SCRUB_TO),
      scenarioLoaded: take(loadScenario.type),
    });

    if (scenarioLoaded) {
      // New scenario loaded — exit playback; autoRunWatcher handles instant run
      break;
    }

    if (restarted) {
      // Replay requested during auto-play — restart
      yield put(simulationStarted());
      continue;
    }

    if (stopped) {
      yield* completeSimulation();
      break;
    }

    if (scrubbed) {
      // Scrub during auto-play — apply it, then keep playing from there
      yield* handleScrub(scrubbed);
      continue;
    }

    if (stepped) {
      // User clicked step during auto-play — execute the requested step and pause
      const stepPos: number = yield select(selectWalkerPosition);
      const stepTotal: number = yield select(selectTotalEvents);
      if (stepPos >= stepTotal) {
        yield* completeSimulation();
        break;
      }
      const stepOk: boolean = yield call(safeExecuteStep, stepPos);
      if (!stepOk) break;
      yield put(simulationPaused());
      continue;
    }
    // paused or modeChanged: loop back to top where status/mode are re-checked
  }
}

// ---------------------------------------------------------------------------
// Instant run — processes all steps with zero delay for pre-computation
// ---------------------------------------------------------------------------

function* instantRun() {
  const totalEvents: number = yield select(selectTotalEvents);
  for (let i = 0; i < totalEvents; i++) {
    const ok: boolean = yield call(safeExecuteStep, i);
    if (!ok) return;
  }
  yield* completeSimulation();
}

/** Fires on every loadScenario — runs the walker instantly so assertions
 *  and actualPath are populated before the user interacts. */
function* handleScenarioLoaded() {
  // Invalidate cached diagnostic topology context for the new scenario
  cachedTopoCtx = null;
  cachedTopoKey = null;
  yield put(simulationStarted());
  yield call(instantRun);
}

/** takeLatest ensures a new scenario load cancels any in-progress run. */
function* autoRunWatcher() {
  yield takeLatest(loadScenario.type, handleScenarioLoaded);
}

// ---------------------------------------------------------------------------
// Main walker saga
// ---------------------------------------------------------------------------

export function* walkerSaga() {
  // Auto-run walker instantly whenever a scenario loads
  yield fork(autoRunWatcher);

  while (true) {
    // Wait for explicit start signal from the user (Play/Step button)
    const startAction: WalkerStartAction = yield take(WALKER_START);
    yield put(simulationStarted());

    // Fast-forward to the requested starting position (no delay)
    const fromPos = startAction.payload?.fromPosition ?? 0;
    let fastForwardOk = true;
    for (let i = 0; i < fromPos; i++) {
      const ok: boolean = yield call(safeExecuteStep, i);
      if (!ok) {
        fastForwardOk = false;
        break;
      }
    }

    try {
      if (fastForwardOk) yield call(playbackLoop);
    } finally {
      const wasCancelled: boolean = yield cancelled();
      if (wasCancelled) {
        yield* completeSimulation();
      }
    }
  }
}

export default walkerSaga;
